import type { EventData, EventSummary } from '@/store/models/events/entities';
import type { EventState } from '@/store/models/events/event-state';
import type { PlannerEventRow, PlannerEventStatus } from '@/types/planner.types';
import { ATTENDEE_TYPE_OPTIONS } from '@/config/bookingFormConfig';

type PlannerMapperInput = EventSummary & Partial<Pick<EventData, 'sponsor' | 'attendeeTypeCode'>>;
import { buildSeriesOrdinalTitleMap } from './seriesEventTitle';
import { formatDate } from './dateFormat';
import { formatUtcDateInTimeZone, formatUtcTimeInTimeZone } from './timeZone';

export const EVENT_STATE_TO_STATUS: Record<EventState, PlannerEventStatus> = {
  DRAFT: 'Pending',
  SUBMITTED: 'New request',
  UNDER_REVIEW: 'Under review',
  CONFIRMED: 'Confirmed',
  DECLINED: 'Declined',
  SUBMITTED_FOR_CANCELLATION: 'Request for cancellation',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
  NO_OCCURRENCE: 'Did not occur',
};

export const STATUS_TO_EVENT_STATE: Record<PlannerEventStatus, EventState> = {
  Pending: 'DRAFT',
  'New request': 'SUBMITTED',
  'Under review': 'UNDER_REVIEW',
  Confirmed: 'CONFIRMED',
  Declined: 'DECLINED',
  'Request for cancellation': 'SUBMITTED_FOR_CANCELLATION',
  Cancelled: 'CANCELLED',
  Completed: 'COMPLETED',
  'Did not occur': 'NO_OCCURRENCE',
};

export function mapStatusesToEventStates(statuses: PlannerEventStatus[]): string {
  return statuses
    .map((s) => STATUS_TO_EVENT_STATE[s])
    .filter(Boolean)
    .join(',');
}

function formatEventDate(startDate: string, endDate: string, timeZone?: string): string {
  const dateStr = formatUtcDateInTimeZone(startDate, timeZone);
  const startTime = formatUtcTimeInTimeZone(startDate, 'en-US', timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const endTime = formatUtcTimeInTimeZone(endDate, 'en-US', timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr} ${startTime} - ${endTime}`;
}

function calculateAgeDays(submittedDate: string | undefined, createdAt: string): number {
  const refDate = submittedDate ?? createdAt;
  const diff = Date.now() - new Date(refDate).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

export function mapEventSummaryToPlannerRow(
  event: PlannerMapperInput,
  timeZoneByBuildingId?: ReadonlyMap<string, string>
): PlannerEventRow {
  const state = event.eventState;
  const status = EVENT_STATE_TO_STATUS[state] ?? 'Pending';
  const ownerName = event.plannerAssignedName ?? 'Unassigned';
  const ownerEmail = event.plannerAssignedEmail?.toLowerCase();
  const timeZone = timeZoneByBuildingId?.get(event.buildingId as string);

  const spaces = event.spaces ?? [];
  const primaryRoom = spaces.length > 0
    ? (spaces[0]!.spaceName ?? '')
    : (event.spaceName ?? '');
  const additionalRooms = spaces.length > 1
    ? spaces.slice(1).map((s) => s.spaceName).filter(Boolean)
    : [];

  return {
    eventId: event.eventId as string,
    eventName: event.eventTitle ?? 'Untitled Event',
    referenceId: (event.requestId as string) ?? (event.eventId as string),
    buildingName: event.buildingName ?? '',
    conferenceRoom: primaryRoom,
    additionalRooms,
    requestedBy: event.submittedByName ?? event.submittedBy ?? event.createdBy,
    fteSponsor: event.sponsor?.name ?? '',
    submittedDate: formatDate(event.submittedDate ?? event.createdAt),
    submittedDateRaw: event.submittedDate ?? event.createdAt ?? '',
    eventDate: formatEventDate(event.eventStartDate, event.eventEndDate, timeZone),
    eventDateRaw: event.eventStartDate ?? '',
    status,
    owner: { name: ownerName, email: ownerEmail },
    attendeeType:
      ATTENDEE_TYPE_OPTIONS.find((o) => o.value === event.attendeeTypeCode)?.labelKey ??
      event.attendeeTypeCode ??
      '',
    ageDays: calculateAgeDays(event.submittedDate, event.createdAt),
    buildingId: (event.buildingId as string) ?? '',
  };
}

export function mapEventSummariesToPlannerRows(
  events: PlannerMapperInput[],
  timeZoneByBuildingId?: ReadonlyMap<string, string>
): PlannerEventRow[] {
  const displayTitleByEventId = buildSeriesOrdinalTitleMap(
    events.map((event) => ({
      eventId: String(event.eventId),
      seriesIndex: event.seriesIndex,
      seriesTotal: event.seriesTotal,
      eventTitle: event.eventTitle ?? 'Untitled Event',
    })),
    (event) => event.eventTitle
  );

  return events.map((event) =>
    mapEventSummaryToPlannerRow(
      {
        ...event,
        eventTitle: displayTitleByEventId.get(String(event.eventId)) ?? event.eventTitle,
      },
      timeZoneByBuildingId
    )
  );
}
